import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Divider,
  Button,
  Drawer,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import {
  Dashboard as DashboardIcon,
  Logout as LogoutIcon,
  People as PeopleIcon,
  Business as BusinessIcon,
  Groups as GroupsIcon,
} from '@mui/icons-material';
import { HugeiconsIcon } from '@hugeicons/react';
import Cancel01Icon from '@hugeicons/core-free-icons/dist/esm/Cancel01Icon';
import { useAuth } from '../context/AuthContext';
import { communityAPI } from '../services/api';

const Navbar: React.FC = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [communityProfileId, setCommunityProfileId] = useState<string | null>(null);
  const [communityProfileIdLoading, setCommunityProfileIdLoading] = useState(false);
  const isHome = location.pathname === '/';
  const isRegisterPage = location.pathname === '/register';
  const isEmployeeDashboard = isAuthenticated && user?.role === 'employee' && location.pathname.startsWith('/dashboard');
  const isPublicCommunityPage =
    location.pathname === '/community' ||
    location.pathname === '/community/submit' ||
    location.pathname === '/community/submit/success' ||
    /^\/community\/(?!me$)(?!admin$)[^/]+$/.test(location.pathname);
  const isPublicPage =
    ['/', '/platform', '/industries', '/contact', '/hr', '/resources'].includes(location.pathname) ||
    location.pathname.startsWith('/resources/') ||
    isPublicCommunityPage;
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 8);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);

    if (user?.role === 'community' && !communityProfileId && !communityProfileIdLoading) {
      setCommunityProfileIdLoading(true);
      communityAPI
        .getMe()
        .then((res) => {
          setCommunityProfileId(res?.data?._id || null);
        })
        .catch(() => {
        })
        .finally(() => {
          setCommunityProfileIdLoading(false);
        });
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMobileMenuOpen = () => {
    setMobileMenuOpen(true);

    if (user?.role === 'community' && !communityProfileId && !communityProfileIdLoading) {
      setCommunityProfileIdLoading(true);
      communityAPI
        .getMe()
        .then((res) => {
          setCommunityProfileId(res?.data?._id || null);
        })
        .catch(() => {
        })
        .finally(() => {
          setCommunityProfileIdLoading(false);
        });
    }
  };

  const handleMobileMenuClose = () => {
    setMobileMenuOpen(false);
  };

  const handleLogout = () => {
    logout();
    handleClose();
    navigate('/login');
  };

  const handleDashboard = () => {
    if (user?.role === 'community') {
      navigate('/community/me');
    } else {
      navigate('/dashboard');
    }
    handleClose();
  };

  const handleUserManagement = () => {
    navigate('/users');
    handleClose();
  };

  const handleOrganizationManagement = () => {
    navigate('/organizations');
    handleClose();
  };

  const handleTeamManagement = () => {
    navigate('/teams');
    handleClose();
  };

  const handleProfile = () => {
    if (user?.role === 'community') {
      navigate('/community/me');
    } else {
      navigate('/profile');
    }
    handleClose();
  };

  const handleCommunityViewProfile = () => {
    if (communityProfileId) {
      navigate(`/community/${communityProfileId}`);
    } else {
      navigate('/community/me');
    }
    handleClose();
  };

  const handleRunSurvey = () => {
    // If already on the landing page, toggle the hash to re-trigger the effect
    if (location.pathname === '/') {
      if (window.location.hash === '#join') {
        window.location.hash = '';
        // Reapply #join on next tick so the effect runs
        setTimeout(() => { window.location.hash = 'join'; }, 0);
      } else {
        window.location.hash = 'join';
      }
    } else {
      navigate('/#join');
    }
  };

  if (isRegisterPage) {
    return null;
  }

  return (
    <AppBar
      className={`navbar ${scrolled ? 'navbar--glass' : ''}`}
      position="fixed"
      sx={{
        zIndex: 900,
        bgcolor: isEmployeeDashboard ? '#ffffff' : 'transparent',
        boxShadow: isEmployeeDashboard ? '0 1px 3px rgba(15, 26, 58, 0.08)' : 'none',
        color: 'text.primary',
        transition: 'background-color 200ms ease, border-color 200ms ease, backdrop-filter 200ms ease',
      }}
    >
      <Toolbar
        className="navbar__toolbar"
        sx={{
          px: isHome ? 8 : 2,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          minHeight: { xs: 56, sm: 64 },
        }}
      >
        <Box
          className="navbar__brand"
          sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
          onClick={() => window.location.href = '/#hr'}
        >
          <Box
            component="img"
            src={`${process.env.PUBLIC_URL}/sglogo2.svg`}
            alt="SuperGauge"
            sx={{ height: 28, display: 'block' }}
          />
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        {/* Show public navigation and auth buttons for non-authenticated users OR authenticated users on public pages */}
        {(!isAuthenticated || (isAuthenticated && isPublicPage)) && (
          <Box
            className="navbar__auth"
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: { xs: 0.5, sm: 2 },
            }}
          >
            <Box
              className="navbar__links"
              sx={{
                display: { xs: 'none', sm: 'flex' },
                alignItems: 'center',
                gap: { xs: 1, sm: 2 },
                mr: { xs: 0, sm: 0.5 },
              }}
            >
              <Button color="inherit" onClick={() => navigate('/platform')}>Platform</Button>
              <Button color="inherit" onClick={() => navigate('/industries')}>Industries</Button>
              <Button color="inherit" onClick={() => navigate('/resources')}>Resources</Button>
            </Box>
            {!isAuthenticated ? (
              <>
                <Button
                  color="inherit"
                  onClick={() => navigate('/login')}
                  sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
                >
                  Log in
                </Button>
                <Button
                  className="navbar__cta"
                  variant="contained"
                  color="primary"
                  onClick={() => navigate('/register')}
                  sx={{ display: { xs: 'none', sm: 'inline-flex' } }}
                >
                  Get started
                </Button>
              </>
            ) : (
              /* Show "Dashboard" link when authenticated on public pages */
              <Button
                color="inherit"
                onClick={() => (user?.role === 'community' ? navigate('/community/me') : navigate('/dashboard'))}
                sx={{ 
                  display: { xs: 'none', sm: 'inline-flex' },
                  textTransform: 'none',
                  fontWeight: 600
                }}
              >
                {user?.role === 'community' ? 'My profile' : 'Dashboard'}
              </Button>
            )}
            <IconButton
              className="navbar__mobile-toggle"
              color="inherit"
              edge="end"
              onClick={handleMobileMenuOpen}
              sx={{ display: { xs: 'inline-flex', sm: 'none' } }}
              aria-label="open navigation menu"
            >
              {mobileMenuOpen ? (
                <HugeiconsIcon icon={Cancel01Icon as any} size={24} strokeWidth={1.8} />
              ) : (
                <MenuIcon />
              )}
            </IconButton>
          </Box>
        )}
        {isAuthenticated && user && (user.role === 'admin' || user.role === 'employee' || user.role === 'community') && !isPublicPage && (
          <Box className="navbar__user" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" sx={{ display: { xs: 'none', sm: 'block' } }}>
              {user.name}
            </Typography>
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenu}
              color="inherit"
            >
              <Avatar className="navbar__avatar" sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
                {user.name.charAt(0).toUpperCase()}
              </Avatar>
            </IconButton>
          </Box>
        )}
        {/* User dropdown menu - available when authenticated regardless of page type */}
        {isAuthenticated && user && (
          <Menu
            className="navbar__menu"
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleClose}
          >
            <MenuItem disabled>
              <Box>
                <Typography variant="body2" fontWeight="bold">
                  {user.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {user.email}
                </Typography>
                <Typography variant="caption" display="block" color="text.secondary">
                  Role: {user.role}
                </Typography>
              </Box>
            </MenuItem>
            <Divider />
            {user?.role === 'community' ? (
              <>
                <MenuItem onClick={handleProfile}>
                  Edit Profile
                </MenuItem>
                <MenuItem onClick={handleCommunityViewProfile} disabled={communityProfileIdLoading}>
                  View Profile
                </MenuItem>
              </>
            ) : (
              <>
                <MenuItem onClick={handleProfile}>
                  My Profile
                </MenuItem>
                <MenuItem onClick={handleDashboard}>
                  <DashboardIcon fontSize="small" sx={{ mr: 1 }} />
                  Dashboard
                </MenuItem>
                {user?.role === 'admin' && (
                  <>
                    <MenuItem onClick={handleTeamManagement}>
                      <GroupsIcon fontSize="small" sx={{ mr: 1 }} />
                      Teams
                    </MenuItem>
                    <MenuItem onClick={handleUserManagement}>
                      <PeopleIcon fontSize="small" sx={{ mr: 1 }} />
                      {user?.role === 'admin' ? 'Users' : 'People'}
                    </MenuItem>
                  </>
                )}
                {user?.role === 'admin' && (
                  <MenuItem onClick={handleOrganizationManagement}>
                    <BusinessIcon fontSize="small" sx={{ mr: 1 }} />
                    Organizations
                  </MenuItem>
                )}
              </>
            )}
            <MenuItem onClick={handleLogout}>
              <LogoutIcon fontSize="small" sx={{ mr: 1 }} />
              Logout
            </MenuItem>
          </Menu>
        )}
      </Toolbar>
      <Drawer
        className="navbar__mobile-drawer"
        anchor="right"
        open={mobileMenuOpen}
        onClose={handleMobileMenuClose}
        PaperProps={{
          className: 'navbar__mobile-drawer-paper',
          sx: {
            width: '100vw',
            maxWidth: '100vw',
          },
        }}
      >
        <Box
          className="navbar__mobile-menu"
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            py: 2,
            px: 2,
            gap: 1,
          }}
        >
          <Box
            className="navbar__mobile-menu-header"
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}
          >
            <Box
              component="img"
              className="navbar__mobile-logo"
              src={`${process.env.PUBLIC_URL}/sglogo.svg`}
              alt="SuperGauge"
              sx={{ 
                height: 24, 
                display: 'block',
                cursor: 'pointer'
              }}
              onClick={() => {
                window.location.href = '/#hr';
                handleMobileMenuClose();
              }}
            />
            <IconButton
              className="navbar__mobile-close"
              onClick={handleMobileMenuClose}
              aria-label="close navigation menu"
            >
              <HugeiconsIcon icon={Cancel01Icon as any} size={24} strokeWidth={1.8} />
            </IconButton>
          </Box>
          <Box className="navbar__mobile-intro" sx={{ mb: 2, fontSize: 14, lineHeight: 1.5, opacity: 0.9 }}>
            Gather the insights you need to make people-driven, data-informed decisions about your company culture.
          </Box>
          <Button
            className="navbar__mobile-item navbar__mobile-item--platform"
            variant="text"
            fullWidth
            onClick={() => {
              navigate('/platform');
              handleMobileMenuClose();
            }}
          >
            Platform
          </Button>
          <Button
            className="navbar__mobile-item navbar__mobile-item--industries"
            variant="text"
            fullWidth
            onClick={() => {
              navigate('/industries');
              handleMobileMenuClose();
            }}
          >
            Industries
          </Button>
          <Button
            className="navbar__mobile-item navbar__mobile-item--resources"
            variant="text"
            fullWidth
            onClick={() => {
              navigate('/resources');
              handleMobileMenuClose();
            }}
          >
            Resources
          </Button>
          <Divider sx={{ my: 1 }} />
          {!isAuthenticated ? (
            <>
              <Button
                className="navbar__mobile-item navbar__mobile-item--login"
                variant="text"
                fullWidth
                onClick={() => {
                  navigate('/login');
                  handleMobileMenuClose();
                }}
              >
                Log in
              </Button>
              <Button
                className="navbar__mobile-item navbar__mobile-item--cta"
                variant="contained"
                fullWidth
                onClick={() => {
                  navigate('/register');
                  handleMobileMenuClose();
                }}
              >
                Get started
              </Button>
            </>
          ) : (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 2, bgcolor: 'rgba(0,0,0,0.04)', borderRadius: 1 }}>
                <Avatar sx={{ width: 40, height: 40, bgcolor: 'secondary.main' }}>
                  {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                </Avatar>
                <Box>
                  <Typography variant="body2" fontWeight="bold">
                    {user?.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {user?.email}
                  </Typography>
                </Box>
              </Box>
              {user?.role === 'community' ? (
                <>
                  <Button
                    className="navbar__mobile-item navbar__mobile-item--dashboard"
                    variant="contained"
                    fullWidth
                    onClick={() => {
                      navigate('/community/me');
                      handleMobileMenuClose();
                    }}
                    sx={{ mb: 1 }}
                  >
                    Edit Profile
                  </Button>
                  <Button
                    className="navbar__mobile-item navbar__mobile-item--dashboard"
                    variant="outlined"
                    fullWidth
                    disabled={communityProfileIdLoading}
                    onClick={() => {
                      navigate(communityProfileId ? `/community/${communityProfileId}` : '/community/me');
                      handleMobileMenuClose();
                    }}
                    sx={{ mb: 1 }}
                  >
                    View Profile
                  </Button>
                </>
              ) : (
                <Button
                  className="navbar__mobile-item navbar__mobile-item--dashboard"
                  variant="contained"
                  fullWidth
                  onClick={() => {
                    navigate('/dashboard');
                    handleMobileMenuClose();
                  }}
                  sx={{ mb: 1 }}
                >
                  Dashboard
                </Button>
              )}
              <Button
                className="navbar__mobile-item navbar__mobile-item--logout"
                variant="outlined"
                fullWidth
                onClick={() => {
                  handleLogout();
                  handleMobileMenuClose();
                }}
              >
                Logout
              </Button>
            </>
          )}
        </Box>
      </Drawer>
    </AppBar>
  );
};

export default Navbar;
